import { Button } from '../../../../../../widgets/buttons';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { TabHeader } from '../../../../../../widgets/tab';
import { useDialogManager } from '../../../../../../widgets/dialog';
import React, { FC } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T } from 'src/shared/helpers';
import Edit from 'src/shared/material_icons/image/edit.svg';
import Play from 'src/shared/material_icons/av/play_arrow.svg';
import Plus from 'src/shared/material_icons/content/add.svg';
import Trash from 'src/shared/material_icons/action/delete.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ComposerActionTreeElement } from '../../../configurator-composer/composer/components/composerActionTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { composerExtensionFieldsQuery } from '../../../configurator-composer/composer/composerQueries';
import { composerQueriesExtensionFieldsQuery } from '../../../configurator-composer/composer/__generated__/composerQueriesExtensionFieldsQuery.graphql';
import ComposerSaveActionDialog from '../../../configurator-composer/composer/dialogs/save/composerSaveActionDialog';
import { setActionSceneElementType, sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { TreeNodeHook } from '../../scene-editors/hooks/useTreeNodeManager';
import { ToolGrid } from '../../toolsgrid';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerEcommercePlatform,
    ComposerExtensionFieldNode,
    ComposerOptionActionNode,
    ComposerPricingNode
} from './composerAttributesModels';

const ComposerActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 38%;
    height: 100%;
    padding: 0 15px;
    padding-top: 0px;
    border-right: 1px solid rgba(0, 0, 0, 0.15);
`;
const ComposerActionsHeader = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding-top: 24px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${theme.app.borderColor};
    min-height: 160px;
    position: relative;
`;

const ComposerActionsBody = styled.div`
    padding: 0px 0;
    flex: 1;
    overflow-y: auto;
    position: relative;
`;
const ComposerActionsList = styled.div<{ $hasActions: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 85%;
    overflow-y: auto;
    gap: 8px;
    position: relative;
    padding-top: 35px;

    ${(props) => !props.$hasActions && 'justify-content: center; align-items: center;'}
`;

const ComposerActionHeaders = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    justify-content: start;
    gap: 50px;
    align-items: center;
    border-bottom: 1px solid ${theme.app.borderColor};
    background: transparent;
    outline: none;
    padding: 10px;
    padding-left: 50px;
    & > div {
        width: 100px;
        text-align: center;
    }
`;

const ComposerActionsSelectorContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 5px;
    left: 0;
    z-index: 10;
    cursor: pointer;
`;
const PropertiesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
`;

const PropertyRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: ${theme.app.backgroundColorBase};
    border: 1px solid ${theme.app.borderColor};
    border-radius: 4px;
`;

const PropertyLabel = styled.span`
    font-weight: 500;
`;

const PropertyValue = styled.span`
    color: ${theme.app.secondary};
`;

const PriceContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
`;

const ComposerActionsSelector: FC<{ selected: boolean; onClick: () => void }> = ({ selected, onClick }) => {
    return (
        <ComposerActionsSelectorContainer onClick={onClick}>
            <Checkbox checked={selected} onChange={onClick} label='Select all items' />
        </ComposerActionsSelectorContainer>
    );
};

interface GlobalField {
    fieldID: string | number | null;
    name: string | null;
    label: string | null;
    dataTypeID: number;
    dataTypeName: string | null;
    operationTypeID: number;
    formatString: string | null;
}

const PropertiesContent: FC<{
    showDialog: (id: string, element: React.ReactElement) => void;
    closeDialog: (id: string) => void;
}> = () => {
    const environment = useRelayEnvironment();
    const selectedOption = useComposerStore((state) => state.selectedOptions?.[0]);
    const updateOption = useComposerStore((state) => state.updateOption);

    const [globalFields, setGlobalFields] = React.useState<GlobalField[]>([]);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValues, setEditValues] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
        fetchQuery<composerQueriesExtensionFieldsQuery>(environment, composerExtensionFieldsQuery, {}).subscribe({
            next: (data) => {
                const fields = (data.extensionFields || []).filter((gf): gf is NonNullable<typeof gf> => gf != null && gf.fieldID != null);
                setGlobalFields(fields);
            }
        });
    }, [environment]);

    if (!selectedOption) {
        return (
            <PropertiesContainer>
                <p>{T._('Select an option to view its properties.', 'Admin')}</p>
            </PropertiesContainer>
        );
    }

    const fields = selectedOption.extensionFields || [];
    const isAdding = fields.length === 0;

    const startEditing = () => {
        const map: Record<string, string> = {};
        fields.forEach((f) => {
            map[String(f.extensionFieldID)] = f.value;
        });
        setEditValues(map);
        setIsEditing(true);
    };

    const handleSave = () => {
        const result: ComposerExtensionFieldNode[] = globalFields
            .filter((gf) => {
                const val = editValues[String(gf.fieldID)];
                return val !== undefined && val !== '';
            })
            .map((gf) => ({
                extensionFieldID: Number(gf.fieldID),
                name: gf.name || '',
                label: gf.label || '',
                value: editValues[String(gf.fieldID)] || '',
                dataTypeID: gf.dataTypeID,
                operationTypeID: gf.operationTypeID,
                formatString: gf.formatString || ''
            }));
        updateOption({ ...selectedOption, extensionFields: result });
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <PropertiesContainer>
                <h4>{isAdding ? T._('Add new properties', 'Admin') : T._('Edit Properties', 'Admin')}</h4>
                {globalFields.length === 0 ? (
                    <p>{T._('No extension fields configured. Configure them in Settings > Additional Properties.', 'Admin')}</p>
                ) : (
                    globalFields.map((gf) => (
                        <FormControl key={String(gf.fieldID)} label={gf.label || gf.name || ''}>
                            <input
                                type='text'
                                placeholder={gf.dataTypeName || '0'}
                                style={{ width: '100%', padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', height: '40px', fontSize: '14px' }}
                                defaultValue={editValues[String(gf.fieldID)] || ''}
                                onChange={(e) => {
                                    setEditValues((prev) => ({ ...prev, [String(gf.fieldID)]: e.target.value }));
                                }}
                            />
                        </FormControl>
                    ))
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <Button onClick={handleSave}>{T._('Save', 'Admin')}</Button>
                    <Button onClick={() => setIsEditing(false)}>{T._('Cancel', 'Admin')}</Button>
                </div>
            </PropertiesContainer>
        );
    }

    return (
        <PropertiesContainer>
            {isAdding ? (
                <>
                    <p>{T._('No properties set for this option.', 'Admin')}</p>
                    <Button onClick={startEditing}>
                        <Icon>
                            <PlusIcon />
                        </Icon>
                        {T._('Add Properties', 'Admin')}
                    </Button>
                </>
            ) : (
                <>
                    {fields.map((f) => (
                        <PropertyRow key={f.extensionFieldID}>
                            <PropertyLabel>{f.label || f.name}</PropertyLabel>
                            <PropertyValue>{f.value}</PropertyValue>
                        </PropertyRow>
                    ))}
                    <Button onClick={startEditing}>{T._('Edit Properties', 'Admin')}</Button>
                </>
            )}
        </PropertiesContainer>
    );
};

const PriceContent: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption?: ComposerAttributeOptionNode;
    onUpdateAttribute: (attribute: ComposerAttributeNode) => void;
}> = ({ selectedAttribute, selectedOption, onUpdateAttribute }) => {
    if (!selectedOption) {
        return (
            <PriceContainer>
                <p>{T._('Select an option to set its price.', 'Admin')}</p>
            </PriceContainer>
        );
    }

    const pricings = selectedAttribute.pricings || [];
    const optionPricing = pricings.find((p) => p.selectedOptionGuid === selectedOption.id);
    const [price, setPrice] = React.useState<string>(optionPricing?.price?.toString() || '');

    React.useEffect(() => {
        const p = (selectedAttribute.pricings || []).find((pr) => pr.selectedOptionGuid === selectedOption.id);
        setPrice(p?.price?.toString() || '');
    }, [selectedOption.id, selectedAttribute.pricings]);

    const handlePriceChange = (value: string) => {
        setPrice(value);
        const numericPrice = parseFloat(value) || 0;
        const existingPricings = [...(selectedAttribute.pricings || [])];
        const existingIndex = existingPricings.findIndex((p) => p.selectedOptionGuid === selectedOption.id);

        const pricingEntry: ComposerPricingNode = {
            attributePricingID: optionPricing?.attributePricingID || 0,
            selectedOptionID: selectedOption.idNumeric || 0,
            selectedOptionGuid: selectedOption.id,
            selectedValue: '',
            price: numericPrice,
            pricingID: optionPricing?.pricingID || 0
        };

        if (existingIndex >= 0) {
            existingPricings[existingIndex] = pricingEntry;
        } else {
            existingPricings.push(pricingEntry);
        }

        onUpdateAttribute({ ...selectedAttribute, pricings: existingPricings });
    };

    return (
        <PriceContainer>
            <FormControl label={T._('Option Price', 'Admin')} tooltipLabelHtml={T._('Set a price adjustment for this option', 'Admin')}>
                <Input type='number' placeholder='0.00' value={price} onChange={(e) => handlePriceChange(e.target.value)} />
            </FormControl>
        </PriceContainer>
    );
};

const ComposerActions: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption?: ComposerAttributeOptionNode;
    actions: ComposerOptionActionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerOptionActionNode>;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
}> = ({ selectedAttribute, selectedOption, actions, multiple, state, materials, meshes, cameras }) => {
    const [zkScene, sceneEditor, zkProduct] = usePreview3DStore((x) => [x.zkScene, x.sceneEditor, x.zkProduct]);
    const [updateAction, removeAction, updateOption, updateAttribute, ecommerceSettings] = useComposerStore((state) => [
        state.updateAction,
        state.removeAction,
        state.updateOption,
        state.updateAttribute,
        state.ecommerceSettings
    ]);
    const { showDialog, closeDialog } = useDialogManager();
    const { filter, setFilter, selectedIds, filteredData, addNode, updateNode, findNodeById, removeNodes, treeState, setSelectedIds } = state;

    const saveAction = (passedAction: ComposerOptionActionNode | null) => {
        showDialog(
            'saveAction',
            <ComposerSaveActionDialog
                attributeID={selectedAttribute.id}
                optionID={passedAction?.optionID || ''}
                onConfirm={(action: ComposerOptionActionNode | null) => {
                    if (!action) return;

                    setActionSceneElementType(action);
                    action.attributeID = selectedAttribute.id;
                    action.optionID = selectedOption?.id || '';
                    if (passedAction) {
                        updateNode(passedAction.id, action);
                    } else {
                        addNode(action);
                    }
                    updateAction(action);
                    closeDialog('saveAction');
                }}
                passedAction={passedAction}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
                edit={!!passedAction}
            />
        );
    };

    const playAction = (action: ComposerOptionActionNode) => {
        sceneEditor?.executeActionsAsync(selectedAttribute.idNumeric, null, [ComposerAttributeNodeAdapter.fromActionNode(action)]);
    };

    const onActionsHeaderButtonClick = (key: string) => {
        switch (key) {
            case 'add':
                saveAction(null);
                break;
            case 'edit':
                if (selectedIds.length !== 1) return;
                const selectedAction = findNodeById(selectedIds[0]);
                saveAction(selectedAction!);
                break;
            case 'remove':
                removeNodes(selectedIds);
                setSelectedIds([]);
                selectedIds.map((id) => {
                    const node = findNodeById(id);
                    if (node) {
                        removeAction(node.attributeID, node.optionID, node.id);
                    }
                });
                break;
            case 'play-selected':
                if (selectedIds.length === 0) return;
                selectedIds.forEach((id) => {
                    const node = findNodeById(id);
                    if (node) {
                        playAction(node);
                    }
                });
                break;
            case 'play':
                if (actions.length === 0) return;
                filteredData.forEach((node) => {
                    playAction(node);
                });
                break;
            default:
                console.warn(`Unknown action: ${key}`);
        }
    };

    const buttons = [
        { icon: <Plus />, name: 'add', tooltip: T._('Add Action', 'Admin'), onClick: () => onActionsHeaderButtonClick('add'), disabled: multiple },
        {
            icon: <Edit />,
            name: 'edit',
            tooltip: T._('Edit Actions', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('edit'),
            disabled: selectedIds.length !== 1 || multiple
        },
        {
            icon: <Trash />,
            name: 'remove',
            tooltip: T._('Remove Action', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('remove'),
            disabled: selectedIds.length === 0
        },
        {
            icon: <Play />,
            name: 'play-selected',
            tooltip: T._('Execute all the selected actions', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('play-selected'),
            disabled: selectedIds.length === 0 || multiple
        },
        {
            icon: <Play />,
            name: 'play',
            tooltip: T._('Execute all the Actions', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('play'),

            disabled: actions.length === 0 || multiple
        }
    ];

    const tabs = [
        { key: 'actions', label: T._('Actions', 'Admin') },
        { key: 'properties', label: T._('Properties', 'Admin') }
    ];
    if (ecommerceSettings?.ecommercePlatform === ComposerEcommercePlatform.WooCommerce) tabs.push({ key: 'price', label: T._('Price', 'Admin') });
    const [activeTab, setActiveTab] = React.useState<string>(tabs[0].key);
    const isActionsTab = activeTab === tabs[0].key;
    const tabSelector = (choice1: any, choice2: any, choice3: any): any => {
        return activeTab === tabs[0].key ? choice1 : activeTab === tabs[1].key ? choice2 : choice3;
    };
    const hasActions = filteredData.length > 0;

    const nodeSelectHandler = (e: React.MouseEvent, node: ComposerOptionActionNode) => {
        const ctrlKey = e.ctrlKey || e.metaKey;
        const shiftKey = e.shiftKey;

        // Se c'è il tasto shift, seleziono un intervallo di nodi
        if (shiftKey) {
            const lastSelectedId = selectedIds[selectedIds.length - 1];
            const lastSelectedNode = findNodeById(lastSelectedId);
            if (!lastSelectedNode) return;

            const startIndex = filteredData.findIndex((n) => n.id === lastSelectedId);
            const endIndex = filteredData.findIndex((n) => n.id === node.id);

            if (startIndex === -1 || endIndex === -1) return;

            const range = filteredData.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            setSelectedIds(range.map((n) => n.id));
            return;
        }

        // con control key, aggiungo o rimuovo l'elemento selezionato
        if (ctrlKey && selectedIds.includes(node.id)) {
            setSelectedIds(selectedIds.filter((id) => id !== node.id));
            return;
        } else if (ctrlKey) {
            setSelectedIds((prev) => [...prev, node.id]);
            return;
        }
        // se non c'è control key, seleziono solo l'elemento corrente
        setSelectedIds([node.id]);
    };

    const moveNodeHandler = (node: ComposerOptionActionNode, direction: 'up' | 'down') => {
        const siblings = filteredData.sort((a, b) => sortByDisplay(filteredData, a, b));

        const currentIndex = siblings.findIndex((n) => n.id === node.id);
        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (swapIndex >= 0 && swapIndex < siblings.length) {
            const swapNode = siblings[swapIndex];
            const currentOrder = node.displayOrder;

            updateNode(node.id, { ...node, displayOrder: swapNode.displayOrder });
            updateNode(swapNode.id, { ...swapNode, displayOrder: currentOrder });
            updateAction({ ...node, displayOrder: swapNode.displayOrder });
            updateAction({ ...swapNode, displayOrder: currentOrder });
        }
    };

    const dragStartHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerOptionActionNode) => {
        e.dataTransfer.setData('text/plain', node.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const dragOverHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerOptionActionNode) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const dropHandler = (e: React.DragEvent<HTMLDivElement>, dropTargetNode: ComposerOptionActionNode | null) => {
        e.preventDefault();
        const draggedNodeId = e.dataTransfer.getData('text/plain');
        const draggedNode = findNodeById(draggedNodeId);

        if (!draggedNode) return;

        if (!dropTargetNode) return;
        const siblings = filteredData.sort((a, b) => sortByDisplay(filteredData, a, b));

        const dropIndex = siblings.findIndex((n) => n.id === dropTargetNode.id);

        // Update dragged node
        updateNode(draggedNode.id, { ...draggedNode, displayOrder: dropIndex });
        updateAction({ ...draggedNode, displayOrder: dropIndex });

        // Update siblings in new parent
        for (let i = dropIndex; i < siblings.length; i++) {
            const sibling = siblings[i];
            updateNode(sibling.id, { ...sibling, displayOrder: i + 1 });
            updateAction({ ...sibling, displayOrder: i + 1 });
        }
    };

    if (multiple) {
        return (
            <ComposerActionsContainer>
                <div>{T._('No action selected.', 'Admin')}</div>
            </ComposerActionsContainer>
        );
    }

    const nodes = filteredData.sort((a, b) => sortByDisplay(filteredData, a, b));

    const content = () => {
        return tabSelector(
            <ComposerActionsList $hasActions={hasActions}>
                {hasActions && (
                    <ComposerActionsSelector
                        selected={selectedIds.length === filteredData.length}
                        onClick={() => {
                            if (selectedIds.length === filteredData.length) {
                                setSelectedIds([]);
                            } else {
                                setSelectedIds(filteredData.map((n) => n.id));
                            }
                        }}
                    />
                )}

                {!hasActions && <div>No actions found</div>}
                {nodes.map((action) => (
                    <ComposerActionTreeElement
                        key={action.id}
                        action={action}
                        selected={selectedIds.includes(action.id)}
                        onSelect={nodeSelectHandler}
                        findMesh={(meshId) => (meshes ?? []).find((mesh) => mesh.id === meshId)}
                        findMaterial={(materialId) => (materials ?? []).find((material) => material.id === materialId)}
                        findCamera={(cameraId) => (cameras ?? []).find((camera) => camera.id === cameraId)}
                        onPlay={(a) => {
                            setSelectedIds([a.id]);
                            playAction(a);
                        }}
                        onDoubleClick={(_, action) => {
                            saveAction(action);
                        }}
                        onMoveDown={() => moveNodeHandler(action, 'down')}
                        onMoveUp={() => moveNodeHandler(action, 'up')}
                        onDragOver={(e) => dragOverHandler(e, action)}
                        onDragStart={(e) => dragStartHandler(e, action)}
                        onDrop={(e) => dropHandler(e, action)}
                    />
                ))}
            </ComposerActionsList>,
            <PropertiesContent showDialog={showDialog} closeDialog={closeDialog} />,
            <PriceContent selectedAttribute={selectedAttribute} selectedOption={selectedOption} onUpdateAttribute={(updated) => updateAttribute(updated)} />
        );
    };

    return (
        <ComposerActionsContainer>
            <ComposerActionsHeader>
                <TabHeader items={tabs} selectedItemKey={activeTab} onTabSelected={(item) => setActiveTab(item.key)} />
                {isActionsTab && <ToolGrid tools={buttons} columns={5} />}
            </ComposerActionsHeader>
            <ComposerActionsBody>
                {hasActions && activeTab === 'actions' && (
                    <>
                        <ComposerActionHeaders>
                            <div>Action</div>
                            <div>Selector</div>
                            <div>Value</div>
                        </ComposerActionHeaders>
                    </>
                )}
                {content()}
            </ComposerActionsBody>
        </ComposerActionsContainer>
    );
};

export default ComposerActions;
